import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useUtteranceLoading } from '../../hooks/useUtteranceLoading';
import { AudioFile, Transcript, EditHistoryHook } from '../../types';
import EditorTranscript from '../transcript/EditorTranscript';
import FileHeader from './FileHeader';
import MediaPlayer from './MediaPlayer';
import TranscriptionProgress from './TranscriptionProgress';

interface FileViewProps {
  audioFile: AudioFile;
  transcript?: Transcript;
  editHistoryHook: EditHistoryHook;
  refreshTrigger?: number;
  onSpeakerStatisticsRefresh: () => void;
}

const FileView: React.FC<FileViewProps> = ({
  audioFile,
  transcript,
  editHistoryHook,
  refreshTrigger,
  onSpeakerStatisticsRefresh,
}) => {
  const [playerCurrentTime, setPlayerCurrentTime] = useState<
    number | undefined
  >(0);
  const [playerIsPlaying, setPlayerIsPlaying] = useState<boolean | undefined>(
    false
  );
  const [transcriptCurrentTime, setTranscriptCurrentTime] = useState<
    number | undefined
  >(0);
  const [transcriptIsPlaying, setTranscriptIsPlaying] = useState<
    boolean | undefined
  >(false);
  const throttleRef = useRef<NodeJS.Timeout | null>(null);
  const visibleUtteranceIdRef = useRef<string | null>(null);

  // Utterance management
  const { utterances, setUtterances, loadingTranscript } = useUtteranceLoading({
    audioFileId: audioFile.id,
    hasTranscript: audioFile.hasTranscript,
    refreshTrigger,
  });

  // Auto-scroll functionality
  const { transcriptContainerRef, handleUserInteraction, updateAutoScroll } =
    useAutoScroll({
      utterances,
      currentTime: playerCurrentTime,
      isPlaying: playerIsPlaying,
    });

  // Intersection observer to track visible utterances
  useEffect(() => {
    if (!transcriptContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleEntries.length > 0) {
          const utteranceId =
            visibleEntries[0].target.getAttribute('data-utterance-id');
          if (utteranceId) {
            visibleUtteranceIdRef.current = utteranceId;
          }
        }
      },
      {
        root: transcriptContainerRef.current,
        rootMargin: '0px 0px -80% 0px',
        threshold: 0,
      }
    );

    // Observe utterance elements
    const observeElements = () => {
      const elements = transcriptContainerRef.current?.querySelectorAll(
        '[data-utterance-id]'
      );
      elements?.forEach((element) => observer.observe(element));
    };

    observeElements();

    // Re-observe when DOM changes
    const mutationObserver = new MutationObserver(observeElements);
    mutationObserver.observe(transcriptContainerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [transcriptContainerRef]);

  // Scroll to visible utterance after mode switch
  useEffect(() => {
    if (!visibleUtteranceIdRef.current || !transcriptContainerRef.current) {
      return;
    }

    const targetUtteranceId = visibleUtteranceIdRef.current;

    // Function to check and scroll to the target utterance
    const scrollToTargetUtterance = () => {
      if (!transcriptContainerRef.current) return false;

      const element = transcriptContainerRef.current.querySelector(
        `[data-utterance-id="${targetUtteranceId}"]`
      );

      if (element) {
        element.scrollIntoView({ behavior: 'auto', block: 'start' });
        return true;
      }
      return false;
    };

    // Try immediately in case elements are already there
    if (scrollToTargetUtterance()) {
      return;
    }

    // Otherwise, watch for the utterance to appear
    const observer = new MutationObserver((mutations, obs) => {
      if (scrollToTargetUtterance()) {
        obs.disconnect();
      }
    });

    observer.observe(transcriptContainerRef.current, {
      childList: true,
      subtree: true,
    });

    // Cleanup - disconnect observer after a reasonable timeout (5 seconds)
    const timeout = setTimeout(() => {
      observer.disconnect();
    }, 5000);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [transcriptContainerRef]);

  // Immediate update player from transcript viewer / editor
  const handlePlayerPlaybackUpdate = useCallback(
    (timestamp: number | undefined, playing: boolean | undefined) => {
      // Cancel any pending throttled updates from media player
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
        throttleRef.current = null;
      }

      setTranscriptCurrentTime(timestamp);
      setTranscriptIsPlaying(playing);
    },
    []
  );

  // Immediate update transcript from media player
  const handleTranscriptPlaybackUpdate = useCallback(
    (timestamp: number, playing: boolean) => {
      setPlayerCurrentTime(timestamp);
      setPlayerIsPlaying(playing);

      updateAutoScroll(timestamp);
    },
    [updateAutoScroll]
  );

  // Delayed update transcript from media player
  const handleTranscriptContinousUpdate = useCallback(
    (timestamp: number, playing: boolean) => {
      // Throttle updates to 100ms intervals to improve performance
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }

      throttleRef.current = setTimeout(() => {
        setPlayerCurrentTime(timestamp);
        setPlayerIsPlaying(playing);

        updateAutoScroll(timestamp);
      }, 100);
    },
    [updateAutoScroll]
  );

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => {
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-shrink-0 space-y-6 mb-6">
        <FileHeader audioFile={audioFile} />

        <MediaPlayer
          mediaFile={audioFile}
          transcriptCurrentTime={transcriptCurrentTime}
          transcriptIsPlaying={transcriptIsPlaying}
          onTranscriptPlaybackUpdate={handleTranscriptPlaybackUpdate}
          onTranscriptContinousUpdate={handleTranscriptContinousUpdate}
        />
      </div>

      <div className="flex-1 min-h-0">
        {audioFile.status === 'processing' ? (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-6">
            <TranscriptionProgress audioFile={audioFile} />
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Your file is being transcribed. This page will update
              automatically when complete.
            </p>
          </div>
        ) : audioFile.hasTranscript ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 h-full flex flex-col">
            <EditorTranscript
              audioFileId={audioFile.id}
              utterances={utterances}
              setUtterances={setUtterances}
              speakers={transcript?.speakers || []}
              transcriptContainerRef={transcriptContainerRef}
              loadingTranscript={loadingTranscript}
              playerCurrentTime={playerCurrentTime}
              playerIsPlaying={playerIsPlaying}
              editHistoryHook={editHistoryHook}
              onPlayerPlaybackUpdate={handlePlayerPlaybackUpdate}
              onSpeakerStatisticsRefresh={onSpeakerStatisticsRefresh}
              onUserInteraction={handleUserInteraction}
            />
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No Transcript Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              This audio file has not been transcribed yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileView;
